'use client'

import { Box, Button, Divider, Flex, Stack, Text } from '@mantine/core'
import { useDisclosure } from '@mantine/hooks'
import { useTranslations } from 'next-intl'

import { useCoinContext } from '@/app/[locale]/price/[slug]/_contexts/CoinContext'
import TokenizedStockIcon from '@/assets/icons/tokenized-stock.svg'
import ResponsiveModal from '@/components/basic/ResponsiveModal'

interface WhyTradeItem {
  title: string
  description: string
}

export default function TokenizedStockBadge() {
  const [opened, { open, close }] = useDisclosure(false)
  const { coin } = useCoinContext()
  const t = useTranslations('tokenizedStockBadge')
  const whyTradeItems = t.raw('whyTradeItems') as WhyTradeItem[]
  const faqLink = `/en/price/stocks/${coin.slug}#faq`

  return (
    <>
      <Flex
        align="center"
        gap={4}
        px={8}
        py={4}
        style={{
          borderRadius: 16,
          background: 'rgba(17, 153, 250, 0.2)',
          cursor: 'pointer',
          flexShrink: 0,
        }}
        onClick={open}
      >
        <TokenizedStockIcon width={16} height={16} />
        <Text variant="subtitle" c="var(--content-active-bold)" style={{ whiteSpace: 'nowrap' }}>
          {t('badgeLabel')}
        </Text>
      </Flex>

      <ResponsiveModal
        opened={opened}
        onClose={close}
        title={t('modalTitle')}
        size={480}
        radius={40}
        styles={{
          header: { position: 'relative' },
          title: {
            position: 'absolute',
            left: '50%',
            transform: 'translateX(-50%)',
            pointerEvents: 'none',
            fontSize: 18,
            fontWeight: 600,
            lineHeight: '32px',
            whiteSpace: 'nowrap',
            color: 'var(--content-primary)',
          },
        }}
      >
        <Stack gap={0}>
          <Box pb={16}>
            <Text variant="heading3" mb={4}>
              {t('whatIsTitle')}
            </Text>
            <Text variant="body" c="var(--content-secondary)">
              {t('whatIsDescription')}
            </Text>
          </Box>
          <Divider mb={16} color="var(--border-default)" />
          <Box pb={8}>
            <Text variant="heading3" mb={12}>
              {t('whyTradeTitle')}
            </Text>
            <Stack gap={16}>
              {whyTradeItems.map(item => (
                <Box key={item.title}>
                  <Text variant="body" fw={600} c="var(--content-primary)" mb={4}>
                    {item.title}
                  </Text>
                  <Text variant="body" c="var(--content-secondary)">
                    {item.description}
                  </Text>
                </Box>
              ))}
            </Stack>
          </Box>
          <Box pt={16}>
            <Button
              component="a"
              href={faqLink}
              radius="md"
              fullWidth
              style={{
                backgroundColor: 'rgba(17, 153, 250, 0.2)',
                color: '#92D1FF',
              }}
            >
              {t('learnMore')}
            </Button>
          </Box>
        </Stack>
      </ResponsiveModal>
    </>
  )
}
